import React from "react";

import Divider from "../../../../../components/shared/Divider/Divider";

/** Replace literal "\\n" with real newlines so the HTML renders line breaks */
function toRenderableHtml(s) {
  return s?.split("\\n").join("\n");
}

const NumberChip = ({ num }) => (
  <span className="relative inline-block">
    <img
      alt=""
      className="block size-[25px]"
      src="/images/pdf-report/rectangle.svg"
    />
    <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs leading-none font-bold text-black">
      {num}
    </span>
  </span>
);

export const ReportMainPage = ({
  club,
  clubSpeed,
  consistencyScore,
  date,
  descriptionSections,
  efficiencyScore,
  overallScore,
  pageNumber = 1,
  pageRef,
  speedScore,
  swingsNumber,
  swingType,
  totalPages,
  userName,
}) => {
  return (
    <div
      className="font-manrope mx-auto my-5 flex h-[1150px] w-[800px] flex-col bg-white p-20"
      ref={pageRef}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-black">Swing Score Report</h2>
        <img alt="logo" className="h-7 w-auto" src="/images/logos/Logo.svg" />
      </div>

      {/* Meta */}
      <div className="flex gap-5 py-5">
        <div className="text-xs text-black">{userName}</div>
        <div className="text-xs text-black">Date: {date}</div>
        <div className="text-xs text-black">Club: {club}</div>
      </div>

      {/* Top KPIs */}
      <div className="flex justify-between py-5">
        {/* Overall score with star */}
        <div className="flex flex-col items-center gap-2.5">
          <div className="relative flex size-[90px] items-center justify-center">
            <img
              alt=""
              className="absolute top-0 left-0 size-full"
              src="/images/pdf-report/star.svg"
            />
            <h1 className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-4xl leading-none font-semibold">
              {overallScore}
            </h1>
          </div>
          <p className="text-grayTrue500 text-xs">Overall Score</p>
        </div>

        {/* Right stats columns */}
        <div className="grid w-full max-w-[55%] grid-cols-3 gap-10">
          <div className="flex flex-col items-start gap-2.5">
            <div className="text-sm font-bold">{swingType}</div>
            <div className="text-grayTrue500 text-[10px]">Swing Type</div>
          </div>

          <div className="flex flex-col items-start gap-2.5">
            <div className="text-sm font-bold">#{swingsNumber}</div>
            <div className="text-grayTrue500 text-[10px]">Swings Taken</div>
          </div>

          <div className="flex flex-col items-start gap-2.5">
            <div className="text-sm font-bold">{clubSpeed}</div>
            <div className="text-grayTrue500 text-[10px]">Avg Club Speed</div>
          </div>

          <div className="flex flex-col items-start gap-2.5">
            <div className="text-sm font-bold">{speedScore}</div>
            <div className="text-grayTrue500 text-[10px]">Speed Score</div>
          </div>

          <div className="flex flex-col items-start gap-2.5">
            <div className="text-sm font-bold">{efficiencyScore}</div>
            <div className="text-grayTrue500 text-[10px]">Efficiency Score</div>
          </div>

          <div className="flex flex-col items-start gap-2.5">
            <div className="text-sm font-bold">{consistencyScore}</div>
            <div className="text-grayTrue500 text-[10px]">
              Consistency Score
            </div>
          </div>
        </div>
      </div>

      <Divider color="gray200" type="horizontal" />

      {/* Sections 01–05 */}
      {[
        { idx: 0, num: "01", title: "Swing Type" },
        { idx: 1, num: "02", title: "Speed Score" },
        { idx: 2, num: "03", title: "Efficiency Score" },
        { idx: 3, num: "04", title: "Consistency Score" },
        { idx: 4, num: "05", title: "Areas of Focus" },
      ].map(({ idx, num, title }, i) => (
        <React.Fragment key={num}>
          <div className="flex justify-between py-5">
            <div>
              <NumberChip num={num} />
              <div className="mt-2.5 text-xs font-bold text-black">{title}</div>
            </div>

            <div
              className="w-full max-w-[68%] text-start text-[10px] leading-5 whitespace-pre-wrap text-black"
              // The HTML is produced by backend templates and already sanitized/curated in legacy.
              // We preserve the legacy behavior (replacing literal "\n" with real newlines).
              dangerouslySetInnerHTML={{
                __html: toRenderableHtml(
                  descriptionSections?.[idx]?.template ?? ""
                ),
              }}
            />
          </div>

          {/* Divider except after the last section */}
          {i < 4 && <Divider color="gray200" type="horizontal" />}
        </React.Fragment>
      ))}

      {/* Footer */}
      <div className="mt-auto flex justify-end">
        <div className="text-grayTrue500 text-[10px]">
          Page {pageNumber} of {totalPages}
        </div>
      </div>
    </div>
  );
};
